import React, { useMemo, useState } from 'react';
import {
  submitPublicGraduationDepositPayment,
  initiatePublicGraduationDepositCardPayment
} from '../../../../services/crmApi';

const METHOD_LABELS = {
  BANK_TRANSFER: 'Bank Transfer',
  CHEQUE: 'Cheque',
  ONLINE_PAYMENT: 'Online Payment',
  MOBILE_PAYMENT: 'Mobile Payment',
  OTHER: 'Other',
  CARD: 'Card (Credit/Debit)'
};

const money = (value, currency) => new Intl.NumberFormat('en-US', { style: 'currency', currency: currency || 'LKR' }).format(Number(value) || 0);

const inputClass = 'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500';

/**
 * Public payment page for graduations that use pass types. Shows only the passes
 * assigned to this student, lets them opt into optional passes (guests, ball) and
 * pay the graduation fees and the ball together or separately.
 */
const PublicGraduationPassPayment = ({ token, details }) => {
  const currency = details.currency;
  const { lines = [], available = [], payable, ball } = details.passes;
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [scope, setScope] = useState('ALL');
  const [form, setForm] = useState({
    paymentMethod: details.paymentMethods?.[0] || '',
    referenceNumber: '',
    bankName: '',
    branchName: '',
    notes: ''
  });

  // Catalogue entries the student controls (optional passes, guest quantities)
  const controlled = available;
  const controlledCodes = new Set(controlled.map((c) => c.passTypeCode));

  const initialQty = useMemo(() => {
    const qty = {};
    controlled.forEach((c) => {
      qty[c.passTypeCode] = lines
        .filter((l) => l.passTypeCode === c.passTypeCode && l.status === 'ASSIGNED' && l.optedIn)
        .reduce((sum, l) => sum + (Number(l.quantity) || 0), 0);
    });
    return qty;
  }, [controlled, lines]);

  const [qty, setQty] = useState(initialQty);

  const fixedItems = (payable?.items || []).filter((i) => !controlledCodes.has(i.passTypeCode));
  const eventOfCode = (code) => (controlled.find((c) => c.passTypeCode === code)?.event) || 'CEREMONY';
  const selectedLines = controlled
    .map((c) => ({ ...c, quantity: Number(qty[c.passTypeCode]) || 0 }))
    .filter((c) => c.quantity > 0)
    .map((c) => ({ name: c.name, passTypeCode: c.passTypeCode, quantity: c.quantity, amount: (c.preview?.amount || 0) * c.quantity, kind: c.kind, event: c.event }));

  const allRows = [
    ...fixedItems.map((i) => ({ name: i.name, quantity: i.quantity, amount: i.amount, kind: i.kind, event: eventOfCode(i.passTypeCode) })),
    ...selectedLines
  ];
  const hasBall = allRows.some((r) => r.event === 'BALL');
  const hasCeremony = allRows.some((r) => r.event !== 'BALL');
  const ballMode = ball?.paymentMode || 'BOTH';
  const effectiveScope = !hasBall || !hasCeremony ? 'ALL' : (ballMode === 'BUNDLED' ? 'ALL' : scope);
  const scoped = allRows.filter((r) => effectiveScope === 'ALL' || (effectiveScope === 'BALL' ? r.event === 'BALL' : r.event !== 'BALL'));
  const total = scoped.reduce((sum, r) => sum + r.amount, 0);
  const paidLines = lines.filter((l) => ['PAID', 'WAIVED'].includes(l.status));

  const setField = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const buildSelections = () => controlled.map((c) => ({
    passTypeCode: c.passTypeCode,
    quantity: Number(qty[c.passTypeCode]) || 0,
    optedIn: (Number(qty[c.passTypeCode]) || 0) > 0
  }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!total) { setError('Select at least one item to pay.'); return; }
    try {
      setSubmitting(true);
      const body = { selections: buildSelections(), scope: effectiveScope };
      if (form.paymentMethod === 'CARD') {
        const res = await initiatePublicGraduationDepositCardPayment(token, body);
        const url = res.data?.data?.paymentPageUrl;
        if (url) window.location.href = url;
        else setError('Could not start card payment. Please try again.');
      } else {
        await submitPublicGraduationDepositPayment(token, { ...form, ...body });
        setSubmitted(true);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to submit payment');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="bg-white rounded-xl shadow p-8 max-w-md w-full text-center">
          <h2 className="text-lg font-semibold text-green-600 mb-2">Payment Submitted</h2>
          <p className="text-gray-700">Your payment of {money(total, currency)} has been submitted and will be reviewed by staff. You will receive a receipt by email once it is approved.</p>
        </div>
      </div>
    );
  }

  const ballClosed = ball?.registrationClosed;

  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl mx-auto bg-white rounded-xl shadow-md overflow-hidden">
        <div className="bg-indigo-600 px-6 py-4">
          <h1 className="text-xl font-bold text-white">Graduation Payment</h1>
          <p className="text-indigo-100 text-sm">{details.graduationName}</p>
          {(details.ceremonyDate || details.venue) && (
            <p className="text-indigo-200 text-xs mt-1">
              {[details.ceremonyDate && new Date(details.ceremonyDate).toLocaleDateString(), details.ceremonyTime, details.venue].filter(Boolean).join(' · ')}
            </p>
          )}
        </div>

        <form onSubmit={onSubmit} className="p-6 space-y-6">
          <div className="border border-gray-200 rounded-xl p-4">
            <h2 className="text-sm font-semibold text-gray-900 mb-1">Student</h2>
            <p className="text-sm text-gray-700">{details.student?.name}</p>
            <p className="text-sm text-gray-500">{[details.student?.email, details.student?.phone].filter(Boolean).join(' · ')}</p>
          </div>

          <div>
            <h2 className="text-sm font-semibold text-gray-900 mb-2">Your fees</h2>
            {fixedItems.length === 0 && controlled.length === 0 && paidLines.length === 0 && (
              <p className="text-sm text-gray-500 bg-gray-50 rounded-lg p-3">There is nothing to pay for this graduation.</p>
            )}
            <div className="divide-y divide-gray-100 border border-gray-200 rounded-xl">
              {fixedItems.map((item) => (
                <div key={item.passId} className="flex items-center justify-between px-4 py-3">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{item.name}{item.quantity > 1 ? ` × ${item.quantity}` : ''}</p>
                    {item.kind === 'REFUNDABLE_DEPOSIT' && <p className="text-xs text-sky-700">Refundable — returned when the cloak is returned</p>}
                    {item.taxAmount > 0 && <p className="text-xs text-gray-500">includes tax {money(item.taxAmount, currency)}</p>}
                  </div>
                  <p className="text-sm font-semibold text-gray-900">{money(item.amount, currency)}</p>
                </div>
              ))}
              {paidLines.map((line) => (
                <div key={line.passId} className="flex items-center justify-between px-4 py-3 bg-green-50">
                  <p className="text-sm text-green-900">{line.name}{line.quantity > 1 ? ` × ${line.quantity}` : ''}</p>
                  <p className="text-xs font-semibold text-green-700 uppercase">{line.status === 'WAIVED' ? 'Waived' : 'Paid'}</p>
                </div>
              ))}
            </div>
          </div>

          {controlled.length > 0 && (
            <div>
              <h2 className="text-sm font-semibold text-gray-900 mb-1">Add-ons</h2>
              {ball && (
                <div className="mb-3 p-3 rounded-lg bg-fuchsia-50 border border-fuchsia-100 text-sm text-fuchsia-900">
                  <p className="font-semibold">{ball.name || 'Graduation Ball'}</p>
                  <p className="text-xs">{[ball.date && new Date(ball.date).toLocaleDateString(), ball.time, ball.venue].filter(Boolean).join(' · ')}</p>
                  {ball.description && <p className="text-xs mt-1">{ball.description}</p>}
                  {ballClosed && <p className="text-xs mt-1 font-semibold text-red-700">Ball registration has closed.</p>}
                  {!ballClosed && ball.registrationDeadline && <p className="text-xs mt-1">Register by {new Date(ball.registrationDeadline).toLocaleDateString()}.</p>}
                </div>
              )}
              <div className="divide-y divide-gray-100 border border-gray-200 rounded-xl">
                {controlled.map((c) => {
                  const paidQty = c.used - (initialQty[c.passTypeCode] || 0);
                  const maxMore = Math.max(0, c.maxPerStudent - paidQty);
                  const closed = c.event === 'BALL' && (ballClosed || !ball);
                  return (
                    <div key={c.passTypeCode} className="flex items-center justify-between gap-3 px-4 py-3">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">{c.name}</p>
                        <p className="text-xs text-gray-500">
                          {money(c.preview?.amount ?? c.price, currency)} each{c.preview?.taxAmount > 0 ? ` (incl. tax ${money(c.preview.taxAmount, currency)})` : ''}
                          {paidQty > 0 ? ` · ${paidQty} already paid` : ''}
                        </p>
                      </div>
                      {maxMore <= 1 ? (
                        <label className="flex items-center gap-2 text-sm">
                          <input
                            type="checkbox"
                            className="h-4 w-4 rounded text-indigo-600"
                            disabled={closed || maxMore === 0}
                            checked={(Number(qty[c.passTypeCode]) || 0) > 0}
                            onChange={(e) => setQty((q) => ({ ...q, [c.passTypeCode]: e.target.checked ? 1 : 0 }))}
                          />
                          Add
                        </label>
                      ) : (
                        <input
                          type="number" min="0" max={maxMore} disabled={closed}
                          value={qty[c.passTypeCode] ?? 0}
                          onChange={(e) => setQty((q) => ({ ...q, [c.passTypeCode]: Math.min(maxMore, Math.max(0, Number(e.target.value) || 0)) }))}
                          className="w-20 border border-gray-300 rounded-lg px-2 py-1 text-sm"
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {hasBall && hasCeremony && ballMode !== 'BUNDLED' && (
            <div>
              <h2 className="text-sm font-semibold text-gray-900 mb-2">What would you like to pay now?</h2>
              <div className="space-y-2">
                {[
                  ['CEREMONY', 'Graduation fees only'],
                  ['BALL', 'Graduation ball only'],
                  ...(ballMode === 'BOTH' ? [['ALL', 'Everything together']] : [])
                ].map(([value, label]) => (
                  <label key={value} className="flex items-center gap-2 text-sm">
                    <input type="radio" name="scope" checked={effectiveScope === value} onChange={() => setScope(value)} /> {label}
                  </label>
                ))}
              </div>
            </div>
          )}

          <div className="bg-indigo-50 rounded-xl p-4 flex items-center justify-between">
            <span className="text-sm text-gray-700">Amount to pay now</span>
            <span className="text-3xl font-bold text-indigo-900">{money(total, currency)}</span>
          </div>

          {details.bankAccount?.accountNumber && form.paymentMethod !== 'CARD' && (
            <div className="border border-gray-200 rounded-xl p-4">
              <h2 className="text-sm font-semibold text-gray-900 mb-2">Bank Account</h2>
              <p className="text-sm text-gray-700">{details.bankAccount.accountName}</p>
              <p className="text-sm text-gray-700">{details.bankAccount.accountNumber}</p>
              <p className="text-sm text-gray-500">{details.bankAccount.bankName} {details.bankAccount.branchName}</p>
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-1">Payment Method</label>
              <select name="paymentMethod" value={form.paymentMethod} onChange={setField} className={inputClass} required>
                <option value="">Select method</option>
                {(details.paymentMethods || []).map((m) => <option key={m} value={m}>{METHOD_LABELS[m] || m}</option>)}
              </select>
            </div>
            {form.paymentMethod && form.paymentMethod !== 'CARD' && (
              <>
                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-1">Reference Number</label>
                  <input name="referenceNumber" value={form.referenceNumber} onChange={setField} className={inputClass} placeholder="Transaction / Cheque / Slip number" required />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <input name="bankName" value={form.bankName} onChange={setField} className={inputClass} placeholder="Bank name" />
                  <input name="branchName" value={form.branchName} onChange={setField} className={inputClass} placeholder="Branch name" />
                </div>
                <textarea name="notes" value={form.notes} onChange={setField} className={inputClass} rows="2" placeholder="Notes (optional)" />
              </>
            )}
          </div>

          {error && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">{error}</p>}

          <button
            type="submit"
            disabled={submitting || !form.paymentMethod || total <= 0}
            className="w-full bg-indigo-600 text-white font-medium py-2.5 px-4 rounded-lg hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            {submitting ? 'Processing...' : form.paymentMethod === 'CARD' ? `Pay ${money(total, currency)} with Card` : 'Submit Payment'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default PublicGraduationPassPayment;
